import { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { Box } from '@infotrack/zenith-ui';
import {
  progressBarContainerStyles,
  interactiveProgressBarContainerStyles,
  progressBarFillStyles,
} from './progress.styles';

type ProgressProps = {
  color: string;
  progressValue: number;
  /**
   * @default "100%"
   */
  width?: string;
  /**
   * @default false
   */
  isInteractive?: boolean;
  onChange?: (value: number) => void;
  /**
   * @default false
   */
  disabled?: boolean;
};

export const Progress = ({
  color,
  progressValue,
  width = '100%',
  isInteractive = false,
  onChange,
  disabled = false,
}: ProgressProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState(progressValue);

  const currentValue = isInteractive && isDragging ? dragValue : progressValue;

  const calculateProgress = useCallback(
    (clientX: number): number => {
      if (!containerRef.current) return progressValue;
      const rect = containerRef.current.getBoundingClientRect();
      const percentage = Math.min(
        100,
        Math.max(0, ((clientX - rect.left) / rect.width) * 100)
      );
      return Math.round(percentage);
    },
    [progressValue]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive || disabled) return;
      e.preventDefault();
      setIsDragging(true);
      const newValue = calculateProgress(e.clientX);
      setDragValue(newValue);
    },
    [isInteractive, disabled, calculateProgress]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      const newValue = calculateProgress(e.clientX);
      setDragValue(newValue);
    },
    [isDragging, calculateProgress]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);
    onChange?.(dragValue);
  }, [isDragging, onChange, dragValue]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isInteractive || disabled || isDragging) return;
      const newValue = calculateProgress(e.clientX);
      onChange?.(newValue);
    },
    [isInteractive, disabled, isDragging, calculateProgress, onChange]
  );

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return undefined;
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const containerStyles = useMemo(() => {
    if (isInteractive) {
      return interactiveProgressBarContainerStyles(width, disabled);
    }
    return { ...progressBarContainerStyles, width };
  }, [isInteractive, width, disabled]);

  const fillStyles = useMemo(
    () => progressBarFillStyles(color, currentValue),
    [color, currentValue]
  );

  const interactiveProps = isInteractive
    ? {
        onMouseDown: handleMouseDown,
        onClick: handleClick,
        tabIndex: disabled ? -1 : 0,
        role: 'slider',
        'aria-valuenow': currentValue,
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-label': 'Progress value',
        'aria-disabled': disabled,
      }
    : {};

  return (
    <Box ref={containerRef} sx={containerStyles} {...interactiveProps}>
      <Box sx={fillStyles} />
    </Box>
  );
};
